import { Link } from 'react-router-dom';

export default function AccessibilityPage() {
  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className="sticky top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 h-20 md:h-24 lg:h-28 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2 md:gap-3 cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/a2144585b096b0c5bc2cbabd4c2b6ea8/e662b63e618aedfff5c098ee5ba35e0a.png" 
                alt="DataVerge Solutions Logo" 
                className="h-16 md:h-24 lg:h-32 w-auto"
              />
            </Link>
          </div>
          
          <div className="hidden md:flex items-center gap-4 lg:gap-10">
            <Link to="/#solutions" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-sm lg:text-base whitespace-nowrap">Solutions</Link>
            <Link to="/#insights" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-sm lg:text-base whitespace-nowrap">Insights</Link>
            <Link to="/#about" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-sm lg:text-base whitespace-nowrap">About</Link>
            <Link to="/#contact" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-sm lg:text-base whitespace-nowrap">Contact</Link>
          </div>

          <Link to="/#contact" className="bg-[#FF6B35] text-white px-4 md:px-6 lg:px-8 py-2 md:py-3 rounded-lg hover:bg-[#000080] transition-all flex items-center gap-2 whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer text-sm md:text-base">
            Get Started
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-[1000px] mx-auto px-6 md:px-12 lg:px-20 py-12 md:py-16 lg:py-20">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-[#000080] mb-6 md:mb-8">Accessibility Statement</h1>
        <div className="text-xs md:text-sm text-[#708090] mb-10 md:mb-12 lg:mb-16">Last Updated: January 2025</div>

        <div className="space-y-8 md:space-y-10 lg:space-y-12 text-[#708090] leading-relaxed">
          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-[#000080] mb-4 md:mb-6">Our Commitment</h2>
            <p className="text-sm md:text-base lg:text-lg mb-3 md:mb-4">
              DataVerge Solutions LLC is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards to ensure we provide equal access to all of our users.
            </p>
            <p className="text-sm md:text-base lg:text-lg">
              We believe that everyone, regardless of ability, should have access to our services and information. Accessibility is not just a legal requirement, it is a core value that reflects our commitment to operational equity and inclusive service delivery.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-[#000080] mb-4 md:mb-6">Conformance Standards</h2>
            <p className="text-sm md:text-base lg:text-lg mb-3 md:mb-4">
              Our website strives to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards. These guidelines explain how to make web content more accessible for people with disabilities and user-friendly for everyone.
            </p>
            <p className="text-sm md:text-base lg:text-lg">
              We are working to achieve and maintain compliance with these standards across all aspects of our digital presence.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-[#000080] mb-4 md:mb-6">Accessibility Features</h2>
            <p className="text-sm md:text-base lg:text-lg mb-3 md:mb-4">Our website includes the following accessibility features:</p>
            <ul className="list-disc pl-6 md:pl-8 space-y-2 text-sm md:text-base lg:text-lg">
              <li><strong className="text-[#FF6B35]">Keyboard Navigation:</strong> All interactive elements can be accessed using keyboard controls</li>
              <li><strong className="text-[#FF6B35]">Screen Reader Compatibility:</strong> Content is structured to work with screen reading software</li>
              <li><strong className="text-[#FF6B35]">Alternative Text:</strong> Images include descriptive alt text for context</li>
              <li><strong className="text-[#FF6B35]">Color Contrast:</strong> Text and background colors meet WCAG contrast requirements</li>
              <li><strong className="text-[#FF6B35]">Responsive Design:</strong> Content adapts to different screen sizes and devices</li>
              <li><strong className="text-[#FF6B35]">Clear Navigation:</strong> Consistent and logical page structure and navigation</li>
              <li><strong className="text-[#FF6B35]">Readable Fonts:</strong> Clear, legible typography with appropriate sizing</li>
              <li><strong className="text-[#FF6B35]">Form Labels:</strong> All form fields have clear, descriptive labels</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-[#000080] mb-4 md:mb-6">Assistive Technologies</h2>
            <p className="text-sm md:text-base lg:text-lg mb-3 md:mb-4">
              Our website is designed to be compatible with the following assistive technologies:
            </p>
            <ul className="list-disc pl-6 md:pl-8 space-y-2 text-sm md:text-base lg:text-lg">
              <li>Screen readers (JAWS, NVDA, VoiceOver)</li>
              <li>Screen magnification software</li>
              <li>Speech recognition software</li>
              <li>Alternative input devices</li>
              <li>Browser accessibility features</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-[#000080] mb-4 md:mb-6">Known Limitations</h2>
            <p className="text-sm md:text-base lg:text-lg mb-3 md:mb-4">
              Despite our best efforts, some content on our website may not yet be fully accessible. We are actively working to address the following known limitations:
            </p>
            <ul className="list-disc pl-6 md:pl-8 space-y-2 text-sm md:text-base lg:text-lg">
              <li>Some third-party embedded content may not be fully accessible</li>
              <li>Certain PDF documents may require accessibility improvements</li>
              <li>Some legacy content is being updated to meet current standards</li>
            </ul>
            <p className="text-sm md:text-base lg:text-lg mt-3 md:mt-4">
              We are committed to addressing these issues and continuously improving accessibility across our entire digital presence.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-[#000080] mb-4 md:mb-6">Ongoing Efforts</h2>
            <p className="text-sm md:text-base lg:text-lg mb-3 md:mb-4">
              DataVerge Solutions is committed to continuous accessibility improvement through:
            </p>
            <ul className="list-disc pl-6 md:pl-8 space-y-2 text-sm md:text-base lg:text-lg">
              <li>Regular accessibility audits and testing</li>
              <li>Staff training on accessibility best practices</li>
              <li>User testing with people who use assistive technologies</li>
              <li>Incorporating accessibility into our design and development processes</li>
              <li>Staying current with evolving accessibility standards and guidelines</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-[#000080] mb-4 md:mb-6">Third-Party Content</h2>
            <p className="text-sm md:text-base lg:text-lg">
              Some content on our website may be provided by third parties. While we strive to ensure all content meets accessibility standards, we may not have full control over third party content. We work with our partners to encourage accessibility compliance wherever possible.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-[#000080] mb-4 md:mb-6">Feedback and Assistance</h2>
            <p className="text-sm md:text-base lg:text-lg mb-3 md:mb-4">
              We welcome your feedback on the accessibility of our website. If you encounter any accessibility barriers or have suggestions for improvement, please let us know:
            </p>
            <ul className="list-disc pl-6 md:pl-8 space-y-2 text-sm md:text-base lg:text-lg mb-4 md:mb-6">
              <li>Describe the accessibility issue you encountered</li>
              <li>Provide the URL or page where you experienced the issue</li>
              <li>Include information about the assistive technology you are using</li>
              <li>Suggest how we might improve accessibility</li>
            </ul>
            <p className="text-sm md:text-base lg:text-lg">
              We will make every effort to respond to accessibility feedback within 5 business days and to provide an accessible alternative or solution as quickly as possible.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-[#000080] mb-4 md:mb-6">Alternative Access</h2>
            <p className="text-sm md:text-base lg:text-lg">
              If you are unable to access any content or functionality on our website due to accessibility barriers, please contact us directly. We will work with you to provide the information or service you need through an alternative communication method that is accessible to you.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-[#000080] mb-4 md:mb-6">Contact Us</h2>
            <p className="text-sm md:text-base lg:text-lg mb-3 md:mb-4">
              For accessibility-related questions, concerns, or feedback, please contact us:
            </p>
            <div className="bg-[#F5F6F7] rounded-xl p-6 md:p-8 space-y-2 md:space-y-3">
              <p className="text-sm md:text-base lg:text-lg"><strong className="text-[#000080]">DataVerge Solutions LLC</strong></p>
              <p className="text-sm md:text-base lg:text-lg"><strong className="text-[#000080]">Accessibility Coordinator</strong></p>
              <p className="text-sm md:text-base lg:text-lg">5306 Six Forks Rd, Ste 107 #1116<br />Raleigh, NC 27609</p>
              <p className="text-sm md:text-base lg:text-lg">Phone: 919-275-0574</p>
              <p className="text-sm md:text-base lg:text-lg">Email: Info@datavergesolutions.com</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-bold text-[#000080] mb-4 md:mb-6">Formal Complaints</h2>
            <p className="text-sm md:text-base lg:text-lg">
              If you are not satisfied with our response to your accessibility concern, you may file a formal complaint with the appropriate regulatory authority in your jurisdiction. We are committed to working cooperatively to resolve any accessibility issues.
            </p>
          </section>
        </div>

        <div className="mt-12 md:mt-16 pt-6 md:pt-8 border-t border-gray-200">
          <Link to="/" className="text-[#FF6B35] hover:text-[#000080] transition-colors flex items-center gap-2 text-base md:text-lg font-medium cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#708090] py-12 md:py-16 mt-12 md:mt-16 lg:mt-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="border-t border-white/20 pt-6 md:pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-xs md:text-sm text-white/80 text-center md:text-left">
                © 2025 DataVerge Solutions LLC. All rights reserved.
              </div>
              <div className="flex items-center gap-4 md:gap-8">
                <Link to="/privacy-policy" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Privacy Policy</Link>
                <Link to="/terms" className="text-xs md:text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Terms</Link>
                <Link to="/accessibility" className="text-xs md:text-sm text-white hover:text-white/80 transition-colors cursor-pointer">Accessibility</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
